/**
 * On-demand browser geolocation (M3-T09). A single fix, opt-in: the visitor
 * presses "Near me", we request one position, and feed the coords to the
 * hospital query so the list sorts nearest-first. Location is never a gate —
 * denial/unavailable leaves the caller free to keep showing the unsorted list.
 */
import { useCallback, useState } from 'react'

import type { GeoPoint } from '../api/hospitals'

export type { GeoPoint }

export type GeoStatus =
  | 'idle'
  | 'prompting'
  | 'granted'
  | 'denied'
  | 'unavailable'

export type Geolocation = {
  status: GeoStatus
  coords: GeoPoint | null
  /** Request a single position fix. No-op while already prompting. */
  request: () => void
}

export function useGeolocation(): Geolocation {
  const [status, setStatus] = useState<GeoStatus>('idle')
  const [coords, setCoords] = useState<GeoPoint | null>(null)

  const request = useCallback(() => {
    if (!('geolocation' in navigator) || !navigator.geolocation) {
      setStatus('unavailable')
      return
    }
    setStatus('prompting')
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude })
        setStatus('granted')
      },
      (err) => {
        // PERMISSION_DENIED === 1; anything else is a position failure.
        setStatus(err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable')
      },
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 60_000 },
    )
  }, [])

  return { status, coords, request }
}
